import awsmobile from '../config/aws-exports';
import { ProductResponse } from '../types/subscription';
import { apiRequestTyped } from '../utils/apiRequestTyped';

/**
 * Constants
 */

const SUBSCRIPTION_NAME = 'Subscribe to Optimise';

/**
 * Interfaces
 */

export interface ActiveSubscription {
  /** The name of the subscription */
  subscription_name: string;

  /** Expiry date of the current subscription period (unix timestamp, includes grace period) */
  subscription_expiry: number;

  /** Renewal attempt date (unix timestamp, retries until expiry if failure) */
  renewal_date: number;

  /** Purchaser of the subscription, either "user" or "installer" */
  purchaser: 'user' | 'installer';

  /** Price paid in the smallest currency unit (e.g. pence for GBP) */
  price: number;

  /** Currency code (ISO 4217, e.g. "GBP", "USD") */
  currency: string;
}

export interface SubscriptionResponse {
  /** The ID attribute of a user */
  user_sub: string;

  /** List of active subscriptions */
  active_subscriptions: ActiveSubscription[];
}

export interface CustomerPortalResponse {
  /** A URL to the customer portal */
  customer_portal_url: string;
}

export interface CheckoutSessionResponse {
  client_secret: string;
}

/**
 * Utils
 */

/**
 * Returns true once `subscription_expiry` has elapsed.
 * Pass `ActiveSubscription.subscription_expiry`, NOT `renewal_date`.
 * The backend already bakes the 7-day grace period into `subscription_expiry`,
 * so no additional offset is applied here.
 */
export const checkSubscriptionExpired = (expirationTimestamp: number | undefined): boolean => {
  if (!expirationTimestamp) return false;

  return expirationTimestamp * 1000 <= Date.now();
};

export const isTrialSubscription = (subscription: ActiveSubscription | undefined): boolean =>
  !!subscription && subscription.price === 0;

/**
 * Requests
 */

export const findActiveSubscription = (subscriptions: ActiveSubscription[]): ActiveSubscription | undefined =>
  subscriptions.find((sub) => sub.subscription_name.toLowerCase() === SUBSCRIPTION_NAME.toLowerCase());

export const getActiveSubscriptions = async (): Promise<SubscriptionResponse> =>
  await apiRequestTyped(`${awsmobile.optimise_endpoint_auth}/subscription/user/active-subscriptions`);

export const getCustomerPortalLink = async (returnUrl?: string): Promise<CustomerPortalResponse> => {
  if (!returnUrl) {
    returnUrl = window.location.href;
  }
  return await apiRequestTyped(
    `${awsmobile.optimise_endpoint_auth}/subscription/user/customer-portal?return_url=${returnUrl}`
  );
};

export const getProducts = async (): Promise<ProductResponse> =>
  await apiRequestTyped(
    `${awsmobile.optimise_endpoint_auth}/subscription/products/available-products?product_name_key=Optimise`
  );

export const getCheckoutSession = async (
  lineItems: string[],
  returnUrl: string,
  canAllowFreeTrials: boolean
): Promise<CheckoutSessionResponse> =>
  await apiRequestTyped(
    `${awsmobile.optimise_endpoint_auth}/subscription/products/checkout-session`,
    undefined,
    'POST',
    {
      line_items: lineItems,
      return_url: returnUrl,
      can_allow_free_trials: canAllowFreeTrials,
    }
  );
